import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './AddCourseDialog.css';

const AddCourseDialog = ({ isOpen, onClose, onSave, faculties }) => {
  const [formData, setFormData] = useState({
    course_name: '',
    course_code: '',
    credit: '',
    faculty_name: '',
    faculty_id: '',
    semester: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({
        course_name: '',
        course_code: '',
        credit: '',
        faculty_name: '',
        faculty_id: '',
        semester: ''
      });
      setError('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // If faculty changes, update faculty_id
    if (name === 'faculty_name' && value) {
      const selectedFaculty = faculties.find(f => f.name === value);
      if (selectedFaculty) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          faculty_id: selectedFaculty._id
        }));
        return;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.course_name.trim() || !formData.course_code.trim() || !formData.credit || !formData.faculty_name || !formData.semester) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    // Convert semester number to semester label
    const semesterLabels = {
      '1': '1st Semester',
      '2': '2nd Semester',
      '3': '3rd Semester',
      '4': '4th Semester',
      '5': '5th Semester',
      '6': '6th Semester',
      '7': '7th Semester',
      '8': '8th Semester'
    };

    const submitData = {
      course_name: formData.course_name.trim(),
      course_code: formData.course_code.trim(),
      credit: parseInt(formData.credit, 10),
      faculty_name: formData.faculty_name,
      faculty_id: formData.faculty_id,
      semester: semesterLabels[formData.semester],
      index: 1 // Fixed index as requested
    };

    try {
      const response = await api.post('/courses', submitData);
      if (response.data.success) {
        onSave(response.data.data);
        onClose();
      } else {
        setError(response.data.message || 'Failed to create course');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Add New Course</h2>
          <button className="close-button" onClick={handleClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="faculty_name">Faculty *</label>
            <select
              id="faculty_name"
              name="faculty_name"
              value={formData.faculty_name}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="">Select faculty</option>
              {faculties.map((faculty) => (
                <option key={faculty._id} value={faculty.name}>
                  {faculty.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="semester">Semester *</label>
            <select
              id="semester"
              name="semester"
              value={formData.semester}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="">Select semester</option>
              <option value="1">1st Semester</option>
              <option value="2">2nd Semester</option>
              <option value="3">3rd Semester</option>
              <option value="4">4th Semester</option>
              <option value="5">5th Semester</option>
              <option value="6">6th Semester</option>
              <option value="7">7th Semester</option>
              <option value="8">8th Semester</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="course_name">Course Name *</label>
            <input
              type="text"
              id="course_name"
              name="course_name"
              value={formData.course_name}
              onChange={handleChange}
              required
              placeholder="Enter course name"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="course_code">Course Code *</label>
            <input
              type="text"
              id="course_code"
              name="course_code"
              value={formData.course_code}
              onChange={handleChange}
              required
              placeholder="Enter course code (e.g., CSE101)"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="credit">Credit Hours *</label>
            <input
              type="number"
              id="credit"
              name="credit"
              value={formData.credit}
              onChange={handleChange}
              required
              min="1"
              max="6"
              placeholder="Enter credit hours (1-6)"
              disabled={loading}
            />
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={handleClose} className="cancel-button" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={loading}>
              {loading ? 'Saving...' : 'Save Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCourseDialog;
